import * as React from "react";
import { DateText } from "shared/components/base";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminUsersPageStyles.scss";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { GridFetchResponse, ValueObject } from "shared/types";
import { CPText } from "shared/components/base";
import UserDetailsModal, { UserRecord } from "./UserDetailsModal";
import InviteUserPanel from "./InviteUserPanel";
import PendingInvitesPanel from "./PendingInvitesPanel";
import useAppState from "shared/state/useAppState";
import { CPGridV2Filters } from "shared/components/shared/CPGridV2/types";
import { useQuery, useQueryClient } from "react-query";
import http from "shared/http";
import { useAgenciesLookupQuery } from "data/lookup/lookupQueries";
import { useSearchUserInvitesQuery } from "data/users/userInvites";
import { Chip } from "@progress/kendo-react-buttons";

interface UseGetUsersForGridQueryOptions extends CPGridV2Filters {
  firstname?: string;
  lastname?: string;
  email?: string;
  isActive?: boolean;
}

const useGetUsersForGridQuery = (params: UseGetUsersForGridQueryOptions) => {
  return useQuery(
    ["users-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.fetchAsync(
        "admin/users/search",
        params,
      );
      return response;
    },
    {
      enabled: true,
      staleTime: 30 * 1000, // Refetch on focus only if data is older than 30s
      refetchOnWindowFocus: true,
    },
  );
};

// Columns
interface ColumnHandlers {
  onEdit: (user: UserRecord) => void;
}

const createColumns = ({ onEdit }: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [{ label: "Edit", icon: "fa-solid fa-pencil" }],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as UserRecord);
    },
  },
  { field: "userId", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "firstname",
    title: "First Name",
    width: 200,
    renderCell: (data: ValueObject) => (
      <CPText style={{ textTransform: "capitalize" }}>
        {data.dataItem.firstname}
      </CPText>
    ),
  },
  {
    field: "lastname",
    title: "Last Name",
    width: 200,
    renderCell: (data: ValueObject) => (
      <CPText style={{ textTransform: "capitalize" }}>
        {data.dataItem.lastname}
      </CPText>
    ),
  },
  { field: "email", title: "Email", width: 340 },
  {
    field: "isActive",
    title: "Status",
    width: 110,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isActive ? "Active" : "Inactive"}
        themeColor={data.dataItem.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    field: "roles",
    title: "Roles",
    width: 240,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.roles ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "agencies",
    title: "Agencies",
    width: 240,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.agencies ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "createdAt",
    title: "Created",
    width: 200,
    renderCell: (data: ValueObject) => (
      <DateText date={data.dataItem.createdAt} />
    ),
  },
  {
    field: "lastLoginAt",
    title: "Last Login",
    width: 200,
    renderCell: (data: ValueObject) => (
      <DateText date={data.dataItem.lastLoginAt} />
    ),
  },
];

// Filter options

// Page
const AdminUsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: agencies, isLoading: isAgenciesLoading } =
    useAgenciesLookupQuery();
  const [filterParams, setFilterParams] =
    React.useState<UseGetUsersForGridQueryOptions>({ take: 20, skip: 0 });

  const {
    data: users,
    isFetching: isUsersLoading,
    refetch,
  } = useGetUsersForGridQuery(filterParams);
  const [selectedUser, setSelectedUser] = React.useState<UserRecord | null>(
    null,
  );
  const [showInvitePanel, setShowInvitePanel] = React.useState(false);
  const [showPendingInvitesPanel, setShowPendingInvitesPanel] =
    React.useState(false);
  const { data: pendingInvites } = useSearchUserInvitesQuery("admin", true);
  const pendingInvitesCount = pendingInvites?.length ?? 0;

  const agencyOptions = React.useMemo(() => {
    return (
      agencies?.map((a) => ({
        label: `${a.agency} - ${a.agencyName} (${a.company})`,
        value: a.agency,
      })) ?? []
    );
  }, [agencies]);

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(() => {
    return [
      {
        filterType: "text",
        label: "First Name",
        columnKey: "firstname",
        placeholder: "e.g. John",
      },
      {
        filterType: "text",
        label: "Last Name",
        columnKey: "lastname",
        placeholder: "e.g. Smith",
      },
      {
        filterType: "email",
        label: "Email",
        columnKey: "email",
        placeholder: "e.g. john@mail.com",
      },
      {
        filterType: "dropdown",
        label: "Status",
        columnKey: "isActive",
        options: [
          { label: "Active", value: true },
          { label: "Inactive", value: false },
        ],
      },
      {
        filterType: "multicheck",
        label: "Agency",
        columnKey: "agencies",
        toType: "array",
        options:
          agencies?.map((x) => ({
            label: x.agency,
            value: x.agency,
          })) ?? [],
      },
    ];
  }, [agencies]);

  const adapter = useCPGridV2({
    columns: createColumns({ onEdit: setSelectedUser }),
    data: users?.records ?? [],
    showLoader: isUsersLoading,
    totalRecords: users?.totalRecords ?? 0,
  });

  // React.useEffect(() => {
  //   setIsLoading(isSavingFilters);
  // }, [isSavingFilters, setIsLoading]);

  return (
    <div className="admin-users-page">
      <CPGridV2
        name="admin-users-grid"
        title="User Management"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetUsersForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        renderActions={() => (
          <>
            <span className="admin-users-page__pending-invites-btn-wrapper">
              <CPButton
                fillMode="flat"
                size="small"
                onClick={() => setShowPendingInvitesPanel(true)}
              >
                Pending Invites
              </CPButton>
              {pendingInvitesCount > 0 && (
                <span
                  className="admin-users-page__pending-invites-badge"
                  title={`${pendingInvitesCount} pending invite${pendingInvitesCount !== 1 ? "s" : ""}`}
                >
                  {pendingInvitesCount}
                </span>
              )}
            </span>
            <CPButton
              fillMode="flat"
              size="small"
              onClick={() => setShowInvitePanel(true)}
            >
              + Invite User
            </CPButton>
            <CPButton
              fillMode="flat"
              size="small"
              onClick={() => setSelectedUser(new UserRecord())}
            >
              + New User
            </CPButton>
          </>
        )}
        onRefresh={() => queryClient.invalidateQueries(["users-grid"])}
      />

      <UserDetailsModal
        key={selectedUser?.userId}
        selectedUser={selectedUser}
        onClose={() => {
          setSelectedUser(null);
        }}
        onSave={() => {
          setSelectedUser(null);
          queryClient.invalidateQueries(["users-grid"]);
        }}
      />

      <InviteUserPanel
        open={showInvitePanel}
        agencyOptions={agencyOptions}
        onClose={() => setShowInvitePanel(false)}
        onSent={() => setShowInvitePanel(false)}
      />

      <PendingInvitesPanel
        open={showPendingInvitesPanel}
        onClose={() => setShowPendingInvitesPanel(false)}
      />
    </div>
  );
};

export default AdminUsersPage;
